import React, { useEffect } from 'react'
import styled from 'styled-components'
import * as yup from 'yup'
import { Formik, FormikHelpers } from 'formik'
import { CardNumberElement } from '@stripe/react-stripe-js'

// Components
import {
  Caption,
  Button,
  Form,
  Banner,
  Modal,
  ModalInjectedProps,
  useStripe,
  Text,
} from '@sketch/components'
import { useToast } from '@sketch/toasts'

// Local components
import CreditCardForm from 'modules/workspace/components/CreditCardForm'
import CreditCards from 'modules/workspace/components/CreditCardLogos'

import { useFlag } from '@sketch/modules-common'

// Validations
import { createFormikValidator } from 'modules/workspace/components/StripeField'

// Types
import {
  useUpdatePaymentDetailsMutation,
  BillingPlanFragment,
} from '@sketch/gql-types'

// Utils
import { confirmPendingSetupIntent, nextPricingDate } from '../../utils'

// Styles
import Fieldset from 'modules/workspace/components/Fieldset'

const Strong = styled.strong`
  color: ${({ theme }) => theme.colors.foreground.secondary.A};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`

const StyledText = styled(Text)`
  && {
    padding-bottom: 8px;
  }
`

// Formik Validation
const VALIDATION_SCHEMA = yup.object().shape({
  cardName: yup.string().trim().required('Name on Card is required'),
})

const INITIAL_VALUES = {
  cardName: '',

  /**
   * These fields are related with the credit-card
   * data, in order to get formik like validation
   * we need to include them in the "initial values"
   *
   * these values are only placeholders, in the end,
   * none of their values will be used
   */
  cardNumber: undefined,
  cardCvc: undefined,
  cardExpirationDate: undefined,
}

type FormValues = typeof INITIAL_VALUES

interface EditPaymentMethodModalProps extends ModalInjectedProps {
  customerId: string
  onPaymentUpdated?: () => Promise<void>
  isWorkspaceSubscriptionActive: boolean
  currentPlan?: BillingPlanFragment
}

const stripeValidation = createFormikValidator(
  ['cardCvc', 'cardExpirationDate', 'cardNumber'],
  {
    cardNumber: 'Card Number is required',
    cardCvc: 'CVC is required',
    cardExpirationDate: 'Expiry Date is required',
  }
)

type PricingChangeBannerProps = {
  currentPlan?: BillingPlanFragment
}

const PricingChangeBanner: React.FC<PricingChangeBannerProps> = ({
  currentPlan,
}) => {
  const isNewPricingFF = useFlag('new_pricing')
  const isNewPricingEnabled =
    isNewPricingFF && currentPlan?.product !== 'BUSINESS'

  const priceRange =
    currentPlan?.type === 'MONTHLY' ? '$9 to $12' : '$99 to $120'

  let dateCopy = ''
  let pricePerEditorCopy = ''
  const date = new Date()
  const nowUTC = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  )

  if (nowUTC < nextPricingDate) {
    dateCopy = 'Starting'
    pricePerEditorCopy =
      currentPlan?.type === 'MONTHLY'
        ? '$9/month per editor'
        : '$99/year per Editor'
  } else {
    dateCopy = 'On'
    pricePerEditorCopy =
      currentPlan?.type === 'MONTHLY'
        ? '$12/month per editor'
        : '$120/year per Editor'
  }
  if (!isNewPricingEnabled) {
    return (
      <Banner type="warning" showIcon={false}>
        Your subscription will be reactivated if you update your card details.
      </Banner>
    )
  }

  return (
    <Banner type="warning" showIcon={false}>
      <StyledText p={0}>
        Your <Strong>{currentPlan?.type?.toLowerCase()} subscription</Strong>{' '}
        will be reactivated and we’ll charge you{' '}
        <Strong>{pricePerEditorCopy}</Strong> if you update your card details.
      </StyledText>
      <StyledText>
        <Strong>{dateCopy} 19 May 2023</Strong>, the price of the{' '}
        {currentPlan?.type?.toLowerCase()} subscription will change from{' '}
        <Strong>{priceRange}</Strong> per editor
      </StyledText>
    </Banner>
  )
}
/**
 * EditPaymentMethodModal
 *
 * Renders the modal that allows the user to edit the payment method
 * in the Workspace Settings > Billing page inside the Payment Details panel
 */
const EditPaymentMethodModal: React.FC<EditPaymentMethodModalProps> = ({
  customerId,
  onPaymentUpdated,
  currentPlan,
  isWorkspaceSubscriptionActive,
  hideModal,
}) => {
  const { showToast } = useToast()
  const { load, status, stripe, elements } = useStripe()

  /* Load stripe */
  useEffect(() => {
    if (status !== 'success') {
      load()
    }
  }, [load, status])

  // MUTATIONS
  const [updatePaymentDetails, { loading }] = useUpdatePaymentDetailsMutation({
    onError: 'show-toast',
  })

  // HANDLERS
  const handleOnSubmit = async (
    values: FormValues,
    actions: FormikHelpers<FormValues>
  ) => {
    if (!stripe || !elements) {
      showToast('Stripe module is not available', 'negative')
      return
    } else {
      const cardElement = elements.getElement(CardNumberElement)
      const { error, token } = await stripe.createToken(cardElement!, {
        name: values.cardName,
      })

      if (error || !token) {
        showToast(
          error?.message || "It wasn't possible to use your credit-card",
          'negative'
        )
      } else {
        let subscription = null

        try {
          const { data } = await updatePaymentDetails({
            variables: {
              input: {
                customerId,
                paymentSource: token.id,
              },
            },
          })

          subscription = data?.updatePaymentDetails.customer.subscription
        } catch (e) {
          // No need to handle the reject promise because onError will take care of it.
        }

        // If there's no subscription that means an error has been thrown
        if (!subscription) {
          return
        }

        /**
         * If "pendingScaToken" is present on the payload we need to do the
         * challenge with stripe to make sure the card is not INCOMPLETE
         */
        if (subscription.pendingScaToken) {
          const response = await confirmPendingSetupIntent(
            stripe,
            subscription?.pendingScaToken
          )

          if (response && response.error) {
            showToast(
              'Unable to complete 3D Secure authentication. Try again, or use a different credit card',
              'negative'
            )
            return
          }
        }

        // We might need to fresh the UI due to the payment update
        await onPaymentUpdated?.()

        if (subscription) {
          showToast('Your payment method has been updated')
          hideModal()
        }
      }

      actions.setSubmitting(false)
    }
  }

  return (
    <Modal onCancel={hideModal}>
      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={VALIDATION_SCHEMA}
        validate={stripeValidation}
        onSubmit={handleOnSubmit}
      >
        {({ isSubmitting, errors }) => (
          <Form>
            <Fieldset disabled={isSubmitting || loading}>
              <Modal.Header>Payment Method</Modal.Header>
              <Modal.Body>
                <CreditCards />
                <CreditCardForm<FormValues> />
                {!isWorkspaceSubscriptionActive && (
                  <PricingChangeBanner currentPlan={currentPlan} />
                )}
                <Caption>
                  <br />
                  This card <Strong>will replace</Strong> your current card and
                  be used on your next bill.
                </Caption>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  disabled={isSubmitting}
                  onClick={hideModal}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={
                    isSubmitting || loading || Object.keys(errors).length > 0
                  }
                  loading={isSubmitting || loading}
                >
                  Update Billing Details
                </Button>
              </Modal.Footer>
            </Fieldset>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default EditPaymentMethodModal
